import { MenuItem } from "lib-klab-core";
import {
  ACCOUNTING_AREA_CODE,
  countAreaModules,
  countAreaViews,
  LOGISTICS_AREA_CODE,
  resolveAreaRoute,
  resolveKlabTimeRoute,
  STAFF_AREA_CODE,
  TECHNOLOGY_AREA_CODE,
} from "./iam-area-access.util";

type Menu = readonly MenuItem[] | null | undefined;

interface WorkspaceAreaCardConfig {
  id: string;
  description: string;
  fallbackRoute: string;
  icon: string;
}

interface WorkspaceAreaConfig {
  name: string;
  contextTitle: string;
  card?: WorkspaceAreaCardConfig;
  resolveAccessRoute: (menu: Menu) => string | null;
  countModules: (menu: Menu) => number;
  countViews: (menu: Menu) => number;
}

function createIamAreaConfig(
  areaCode: string,
  config: Omit<WorkspaceAreaConfig, "resolveAccessRoute" | "countModules" | "countViews">,
): WorkspaceAreaConfig {
  return {
    ...config,
    resolveAccessRoute: (menu) => resolveAreaRoute(menu, areaCode),
    countModules: (menu) => countAreaModules(menu, areaCode),
    countViews: (menu) => countAreaViews(menu, areaCode),
  };
}

const workspaceAreaConfig = {
  accounting: createIamAreaConfig(ACCOUNTING_AREA_CODE, {
    name: "Contabilidad y Finanzas",
    contextTitle: "Contabilidad",
    card: {
      id: "accounting",
      description: "Gestiona la contabilidad, finanzas, pagos y reportes.",
      fallbackRoute: "/admin/accounting",
      icon: "icon_payslips",
    },
  }),
  logistics: createIamAreaConfig(LOGISTICS_AREA_CODE, {
    name: "Logística y Almacén",
    contextTitle: "Logística",
    card: {
      id: "logistics",
      description: "Controla inventarios, compras, almacenes y proveedores.",
      fallbackRoute: "/admin/logistics",
      icon: "icon_element",
    },
  }),
  "klab-time": {
    name: "Klab time",
    contextTitle: "Time",
    resolveAccessRoute: resolveKlabTimeRoute,
    countModules: () => 0,
    countViews: () => 0,
  },
  staff: createIamAreaConfig(STAFF_AREA_CODE, {
    name: "Recursos \n Humanos",
    contextTitle: "Staff",
    card: {
      id: "rrhh",
      description: "Ingresa a la gestión habitual de colaboradores y equipos.",
      fallbackRoute: "/admin/staff/dashboard",
      icon: "icon_people",
    },
  }),
  technology: createIamAreaConfig(TECHNOLOGY_AREA_CODE, {
    name: "Tecnología e innovación",
    contextTitle: "Tecnología",
    card: {
      id: "technology",
      description: "Gestiona proyectos, tickets, infraestructura y soporte TI.",
      fallbackRoute: "/admin/technology",
      icon: "icon_settings",
    },
  }),
} satisfies Record<string, WorkspaceAreaConfig>;

export type WorkspaceArea = keyof typeof workspaceAreaConfig;

export const WORKSPACE_AREA_CONFIG: Readonly<
  Record<WorkspaceArea, WorkspaceAreaConfig>
> = workspaceAreaConfig;

export const WORKSPACE_AREAS = Object.keys(
  WORKSPACE_AREA_CONFIG,
) as WorkspaceArea[];

export function resolveWorkspaceAreaRoute(
  menu: Menu,
  area: WorkspaceArea,
): string | null {
  return WORKSPACE_AREA_CONFIG[area].resolveAccessRoute(menu);
}

export function hasWorkspaceAreaAccess(
  menu: Menu,
  area: WorkspaceArea,
): boolean {
  return resolveWorkspaceAreaRoute(menu, area) !== null;
}

export function createWorkspaceAreaCards(menu: Menu) {
  return WORKSPACE_AREAS.flatMap((area) => {
    const config = WORKSPACE_AREA_CONFIG[area];
    if (!config.card) return [];

    const route = resolveWorkspaceAreaRoute(menu, area);
    return [
      {
        id: config.card.id,
        name: config.name,
        description: config.card.description,
        route: route ?? config.card.fallbackRoute,
        icon: config.card.icon,
        modules: config.countModules(menu),
        views: config.countViews(menu),
        enabled: route !== null,
      },
    ];
  });
}
